import classNames from "classnames";
import { DateTime } from "luxon";
import { useHistory } from "react-router";
import { CalendarEventDisplayData, days } from "./calendar_page";
import { CalendarEventItem } from "./calendar_event_item";
import { useServerId } from "../../../../hooks/use_server_id";
import { common_utils } from "../../../../../../../../../common/common_utils";
import { EventDisplayData } from "../../../../../../../../../common/sesh_types";
import { WithDropdownBase } from "../../../../base_components/with_dropdown_base";
import { BoxItem } from "../../../../box_item";
import { useHistoryPusher } from "../hooks/use_history_pusher";
import { getEventId } from "../utils/get_event_id";

export function CalendarCell(props: {
	index: number;
	day_offset: number;
	start_of_month: DateTime;
	events: CalendarEventDisplayData[];
}) {
	const date = props.start_of_month
		.plus({ days: props.day_offset })
		.startOf("day");

	const has_weekday_shown = props.index < 7;

	const max_events_to_display = has_weekday_shown ? 4 : 5;

	const events_to_display = props.events.slice(0, has_weekday_shown ? 4 : 5);

	const dropdown_id = `calendar-cell-${props.day_offset}`;

	const now = DateTime.now().startOf("day");

	const is_today = now.toMillis() === date.toMillis();
	const is_in_past = date.toMillis() < now.toMillis();
	const is_first_of_month = date.day === 1;

	const history = useHistory();
	const server_id = useServerId();

	let day_element: JSX.Element | undefined;
	if (is_today) {
		day_element = (
			<span className="flex w-[20px] flex-col items-center rounded-[100%] bg-sesh-primary font-bold text-black">
				{date.day}
			</span>
		);
	} else if (is_first_of_month) {
		day_element = (
			<span className="font-bold">
				{date.monthShort} {date.day}
			</span>
		);
	} else {
		day_element = <span className="font-bold">{date.day}</span>;
	}

	const pushHistory = useHistoryPusher();

	return (
		<div
			key={props.day_offset}
			className={classNames(
				"group/cell h-full border-b border-r border-sesh-dark bg-base-dark",
				{
					"border-l": props.index % 7 === 0,
					"border-t": props.index < 7,
				}
			)}
		>
			<div
				className={classNames(
					"flex h-full flex-col items-center text-sm",
					{
						"opacity-50":
							is_in_past ||
							props.start_of_month.month !== date.month,
					}
				)}
			>
				<div
					className={classNames(
						"group/day relative flex w-full py-[2px] transition-all",
						{
							"cursor-pointer hover:bg-base-light": !is_in_past,
						}
					)}
					onClick={() => {
						if (is_in_past) {
							return;
						}

						pushHistory(
							common_utils.getEventCreateURL(
								server_id,
								undefined,
								undefined,
								{
									date: encodeURIComponent(
										date
											.set({ hour: 12 })
											.toISO()
											.replaceAll("+", "%2b")
									),
								}
							)
						);
					}}
				>
					<div className="flex w-full flex-col items-center">
						{has_weekday_shown && <span>{days[props.index]}</span>}
						{day_element}
					</div>
					{!is_in_past && (
						<div className="absolute flex w-full px-1 opacity-0 transition-all group-hover/cell:opacity-25 group-hover/day:!opacity-100">
							<span className="ml-auto">
								<i className="fas fa-plus"></i>
							</span>
						</div>
					)}
				</div>
				<div className="flex h-full w-full flex-col gap-0 overflow-y-auto px-1">
					{events_to_display.map((val) => (
						<CalendarEventItem
							key={`${getEventId(val)}_${val.start_time_str}`}
							is_today_or_future={!is_in_past}
							event={val}
						/>
					))}
					{props.events.length > max_events_to_display && (
						<WithDropdownBase
							id={dropdown_id}
							dropdown_element={
								<BoxItem
									bgColor={"semidark"}
									className="border border-sesh-primary"
								>
									<div className="flex w-48 flex-col gap-2">
										<div className="flex w-full flex-col items-center gap-0">
											<span className="text-xs text-content-secondary">
												{date.weekdayLong}
											</span>
											<span className="text-lg font-bold">
												{date.day}
											</span>
										</div>
										<div className="flex max-h-32 flex-col gap-1 overflow-auto">
											{props.events.map((val) => (
												<CalendarEventItem
													key={`${getEventId(val)}_${
														val.start_time_str
													}`}
													is_today_or_future={
														!is_in_past
													}
													event={val}
													dropdown_parent_id={
														dropdown_id
													}
												/>
											))}
										</div>
									</div>
								</BoxItem>
							}
						>
							<div className="flex w-full flex-shrink-0 cursor-pointer gap-2 overflow-hidden overflow-ellipsis whitespace-nowrap px-1 text-xs hover:bg-base-light">
								{props.events.length - max_events_to_display}{" "}
								more
							</div>
						</WithDropdownBase>
					)}
				</div>
			</div>
		</div>
	);
}
